// Plan-status resolution for the persistent status bar, the derived
// "rest targets set" feature flag, and the annual-planner flag.
// Pure functions over plan / athlete records.

import { getDateToDisplay } from "../nutrition-helpers";
import { PLAN_STATUS } from "./constants";

// kcal derived from macros — never trust a stored calories field blindly,
// but fall back to it when macros are absent (P×4 + C×4 + F×9).
const kcalFromMacros = (m) =>
  Math.round((m.proteins || 0) * 4 + (m.carbs || 0) * 4 + (m.fats || 0) * 9);

const resolveKcal = (source) =>
  source ? source.calories || kcalFromMacros(source) : null;

// A plan has rest-day targets when its restDay block carries real numbers.
export const hasRestTargets = (plan) => Number(plan?.restDay?.calories) > 0;

// A plan is backed by an in-app meal plan when it references one.
// The API populates mealPlanId → mealPlan (with { _id, published }) so we check mealPlan._id.
export const hasMealPlan = (plan) => !!plan?.mealPlan?._id;

// Whether a plan's end date hasn't passed yet — the single source of truth
// for "is this plan still current", used everywhere DUE is derived and by
// any UI that should stop offering plan-scoped actions once a plan has ended
// (e.g. building a meal plan for a plan that's already over).
export const isPlanCurrent = (plan, todayUtc) => plan.utcEndDate >= todayUtc;

// Map the active / most-recent plan to one of the four status states.
// activePlan = plan covering today; recentPlan = latest-ending plan overall.
export const resolvePlanStatus = ({ activePlan, recentPlan, todayUtc }) => {
  if (activePlan) {
    return hasMealPlan(activePlan)
      ? PLAN_STATUS.ACTIVE_MEAL
      : PLAN_STATUS.ACTIVE;
  }
  if (recentPlan && !isPlanCurrent(recentPlan, todayUtc)) {
    return PLAN_STATUS.DUE;
  }
  return PLAN_STATUS.NONE;
};

// Display status for a single plan card in the list. Unlike resolvePlanStatus
// (which answers "what covers the selected range"), this classifies one plan:
// an ended plan is DUE; otherwise ACTIVE_MEAL / ACTIVE by whether it has a meal plan.
export const resolvePlanStatusForPlan = (plan, todayUtc) => {
  if (!isPlanCurrent(plan, todayUtc)) return PLAN_STATUS.DUE;
  return hasMealPlan(plan) ? PLAN_STATUS.ACTIVE_MEAL : PLAN_STATUS.ACTIVE;
};

// Whether a plan matches a status-filter chip. The chips span two independent
// axes — lifecycle (active/due) and meal-plan (yes/no) — so "No meal plan"
// matches any plan without a meal plan regardless of lifecycle.
export const planMatchesStatusFilter = (plan, filter, todayUtc) => {
  if (!filter) return true;
  const isDue = !isPlanCurrent(plan, todayUtc);
  switch (filter) {
    case PLAN_STATUS.ACTIVE_MEAL:
      return !isDue && hasMealPlan(plan);
    case PLAN_STATUS.ACTIVE:
      return !hasMealPlan(plan);
    case PLAN_STATUS.DUE:
      return isDue;
    default:
      // PLAN_STATUS.NONE — no real plan in the list is "none"
      return false;
  }
};

// Annual-planner flag: true when the athlete has at least one phase on
// either track (training or nutrition) - both render their own band row in
// the Calendar view and their own transition markers in the Summary chart,
// so an athlete with only nutrition phases still counts as having a planner.
// `athlete.annualPlan` is already normalized server-side (ensure-coach.js
// loads it through msb-api's switch-aware periodization endpoint, which
// always answers in this exact legacy shape) - no local normalization needed
// here anymore. Returns false when annualPlan is absent entirely (intended
// empty state).
export const hasAnnualPlanner = (athlete) =>
  (athlete?.annualPlan?.trainingPhases?.phases?.length || 0) > 0 ||
  (athlete?.annualPlan?.nutritionPhases?.phases?.length || 0) > 0;

// Everything the status bar needs to describe the plan it measures against.
export const getPlanTargetsSummary = (plan) => {
  if (!plan) return null;
  return {
    label: `${getDateToDisplay(plan.utcStartDate)} - ${getDateToDisplay(plan.utcEndDate)}`,
    startDate: getDateToDisplay(plan.utcStartDate),
    endDate: getDateToDisplay(plan.utcEndDate),
    trainingKcal: resolveKcal(plan),
    restKcal: resolveKcal(plan.restDay),
    protein: plan.proteins || 0,
    hasMeal: hasMealPlan(plan),
    hasRest: hasRestTargets(plan),
  };
};
